import { getAllScopedLineItems, ScanRootOptimized } from '@kaiizen/kai-scan-template-system';
import { useCachedQuery } from '@kaiizen/react-persistent-cache';
import { useMemo } from 'react';
import { useAllProductSupplierOrgs } from '../apiState/useAllProductSupplierOrgs';
import { useAuth } from '../useAuth';
import { useKaiProApiClient } from '../useKaiProApiClient';

export type OrgProductExistsLookup = (orgProductId: string) => boolean | undefined;

/**
 * Collects org product ids from scoped line items that have productSupplierId and skuDetails.id.
 * Same scoping as scope report: isGroupShownOnScopeReport, isTaskShownOnScopeReport, isOptionScoped, isLineItemScoped.
 */
const getScopedOrgProductIds = (optimizedScan: ScanRootOptimized, thdOrgId?: string): string[] => {
  const lineItems = getAllScopedLineItems(optimizedScan);
  // currently, we only support THD products in the catalog
  // so specifically exclude hdSupply products or we get false positives
  const ids = lineItems
    .filter(lineItem =>
      !!thdOrgId
      && lineItem.skuDetails?.productSupplierId != null
      && lineItem.skuDetails?.id != null
      && lineItem.skuDetails.productSupplierId === thdOrgId
    )
    .map(lineItem => lineItem.skuDetails!.id!);
  return [...new Set(ids)];
};

export const useOrgProductLookup = (optimizedScan: ScanRootOptimized | undefined) => {
  const auth = useAuth();
  const kaiProApiClient = useKaiProApiClient();
  const { productSupplierOrgs, productSuppliersLoading } = useAllProductSupplierOrgs();
  const thdOrgId = productSupplierOrgs?.homeDepot?.id;

  const orgProductIds = useMemo(
    () => (
      optimizedScan && !productSuppliersLoading ? getScopedOrgProductIds(optimizedScan, thdOrgId) : []
    ),
    [optimizedScan, productSuppliersLoading, thdOrgId],
  );

  const queryKey = useMemo(
    () => (orgProductIds.length > 0 ? `orgProductExistsLookup/${auth.orgId ?? 'none'}/${[...orgProductIds].sort().join(',')}` : null),
    [auth.orgId, orgProductIds],
  );

  const query = useCachedQuery<Record<string, boolean>>(
    queryKey ?? 'orgProductExistsLookup/disabled',
    (_key, _queryCache) =>
      kaiProApiClient.getOrgProductBatch(orgProductIds).then(products => {
        const inCatalogById = new Map(
          products
            .filter(product => product.thdStoreSku != null && product.thdStoreSku !== '')
            .map(product => [product.id, true] as const),
        );
        return Object.fromEntries(orgProductIds.map(id => [id, inCatalogById.get(id) === true]));
      }),
    { enabled: !!queryKey && !!kaiProApiClient.token && !!auth.orgId },
  );

  const getOrgProductExists: OrgProductExistsLookup = useMemo(() => {
    const map = query.data ?? {};
    return (orgProductId: string) => map[orgProductId];
  }, [query.data]);

  return {
    getOrgProductExists,
    orgProductExistsMap: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
};
